import { useCallback, useEffect, useState } from "react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { EmptyState, PageHeader, Spinner } from "@/components/bits";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RefreshCw, ScanFace } from "lucide-react";

const EVENTO = {
  enroll: { label: "Cadastro", cls: "text-emerald-400 bg-emerald-400/10" },
  alterado: { label: "Alteração da foto", cls: "text-sky-400 bg-sky-400/10" },
  removido: { label: "Exclusão", cls: "text-rose-400 bg-rose-400/10" },
  verify_negado: { label: "Verificação negada", cls: "text-amber-400 bg-amber-400/10" },
  verify_ok: { label: "Verificação aprovada", cls: "text-teal-400 bg-teal-400/10" },
};

export default function FacialReportPage() {
  const [dados, setDados] = useState(null);
  const [evento, setEvento] = useState("todos");
  const [erro, setErro] = useState("");

  const carregar = useCallback(async () => {
    setErro("");
    try {
      const { data } = await api.get("/tenant/facial/relatorio");
      setDados(data);
    } catch (e) {
      setErro(errMsg(e));
      setDados({ items: [], total: 0 });
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const itens = (dados?.items || []).filter((i) => evento === "todos" || i.evento === evento);

  return (
    <div data-testid="facial-report-page">
      <PageHeader title="Reconhecimento Facial (2FA)"
        subtitle="Trilha LGPD do cadastro facial: quem cadastrou, alterou ou excluiu a foto, e verificações negadas para calibragem do threshold do piloto">
        <Button variant="outline" size="sm" onClick={carregar} data-testid="facial-report-atualizar"
          className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
          <RefreshCw className="h-4 w-4 mr-1" /> Atualizar
        </Button>
      </PageHeader>

      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-3 mb-4 flex items-end gap-4"
        data-testid="facial-report-filtros">
        <div>
          <Label className="text-xs text-zinc-400">Evento</Label>
          <Select value={evento} onValueChange={setEvento}>
            <SelectTrigger data-testid="facial-report-filtro-evento"
              className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md w-52">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
              <SelectItem data-testid="facial-report-evento-todos" value="todos">Todos</SelectItem>
              {Object.entries(EVENTO).map(([k, v]) => (
                <SelectItem key={k} data-testid={`facial-report-evento-${k}`} value={k}>{v.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <p className="text-xs text-zinc-500 pb-1">
          Verificações negadas mostram o score do matching — úteis para recalibrar o threshold durante o piloto.
        </p>
      </div>

      {!dados && <Spinner />}
      {dados && erro && (
        <EmptyState icon={ScanFace} title="Não foi possível carregar o relatório" subtitle={erro} />
      )}
      {dados && !erro && itens.length === 0 && (
        <EmptyState icon={ScanFace} title="Nenhum evento facial registrado"
          subtitle="Cadastros, alterações, exclusões e verificações do appmobile aparecem aqui." />
      )}
      {dados && !erro && itens.length > 0 && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-md overflow-x-auto">
          <table className="w-full text-sm" data-testid="facial-report-tabela">
            <thead>
              <tr className="text-left text-xs text-zinc-400 border-b border-zinc-800">
                <th className="px-4 py-3 font-medium">Data/Hora</th>
                <th className="px-4 py-3 font-medium">Morador</th>
                <th className="px-4 py-3 font-medium">Vínculo</th>
                <th className="px-4 py-3 font-medium">Evento</th>
                <th className="px-4 py-3 font-medium">Detalhe</th>
              </tr>
            </thead>
            <tbody>
              {itens.map((i) => {
                const ev = EVENTO[i.evento] || { label: i.evento, cls: "text-zinc-400 bg-zinc-400/10" };
                return (
                  <tr key={i.id} className="border-b border-zinc-800/60 last:border-0"
                    data-testid={`facial-report-row-${i.id}`}>
                    <td className="px-4 py-3 text-zinc-300 whitespace-nowrap">{fmtDate(i.quando)}</td>
                    <td className="px-4 py-3 text-zinc-100 font-medium">{i.morador || "—"}</td>
                    <td className="px-4 py-3 text-zinc-300 capitalize">{i.vinculo || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded text-[11px] font-medium ${ev.cls}`}
                        data-testid={`facial-report-evento-label-${i.id}`}>{ev.label}</span>
                    </td>
                    <td className="px-4 py-3 text-zinc-400 text-xs">{i.detalhe}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
